/**
 * Date utility functions for consistent timezone handling across the app.
 * Date-only strings (YYYY-MM-DD) are treated as local dates to avoid timezone shift.
 * Full datetime strings are converted from UTC to local time.
 */

/**
 * Parse a date string, treating date-only strings as local time
 * @param {string|Date} date - Date string or Date object
 * @returns {Date} Parsed date
 */
function parseDate(date) {
  if (!date) return null;
  if (date instanceof Date) return date;
  // Date-only format (YYYY-MM-DD): treat as local date to avoid timezone shift
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  // D1's datetime('now') returns "YYYY-MM-DD HH:MM:SS" in UTC with no zone
  // marker; new Date() would parse that as LOCAL time. Normalize to ISO UTC.
  if (
    typeof date === "string" &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(date)
  ) {
    return new Date(`${date.replace(" ", "T")}Z`);
  }
  return new Date(date);
}

/**
 * Format a date string for display (e.g., "Jan 4, 2026")
 * @param {string|Date} date - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export function formatDate(
  date,
  options = { month: "short", day: "numeric", year: "numeric" },
) {
  const d = parseDate(date);
  if (!d) return "";
  return d.toLocaleDateString("en-US", options);
}

/**
 * Format a date string for HTML date input (YYYY-MM-DD)
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateForInput(date) {
  const d = parseDate(date);
  if (!d) return "";
  return d.toLocaleDateString("en-CA"); // en-CA gives YYYY-MM-DD format
}

/**
 * Format a date and time for display (e.g., "Jan 4, 2026, 3:45 PM")
 * @param {string|Date} date - UTC date string or Date object
 * @returns {string} Formatted date and time in user's local timezone
 */
export function formatDateTime(date) {
  const d = parseDate(date);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a time for display (e.g., "3:45 PM")
 * @param {string|Date} date - UTC date string or Date object
 * @returns {string} Formatted time in user's local timezone
 */
export function formatTime(date) {
  const d = parseDate(date);
  if (!d) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a relative time (e.g., "5m ago", "2d ago")
 * @param {string|Date} date - UTC date string or Date object
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
  const d = parseDate(date);
  if (!d) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}
